/**
 * Cross-tab tier-upgrade sync — PII-free localStorage signalling.
 *
 * When checkout opens in a second tab, the original tab needs to auto-refresh to
 * the Pro state once payment completes. This module holds the two purpose-built
 * localStorage keys that coordinate that, plus their read/write helpers. The
 * actual listening/reconcile logic lives in `hooks/useTierUpgradeSync.ts`.
 *
 * Design (CDO + Disruptor council): the localStorage `storage` event is the single
 * cross-tab primitive (a persistent key survives a suspended/discarded tab, unlike
 * a live BroadcastChannel message). Both keys carry ONLY a millisecond timestamp —
 * no tier, no user id, no token — so nothing identifying crosses the tab boundary.
 * `GET /me` (via authStore.checkAuth) stays the sole source of truth; these signals
 * are just a "go reconcile" doorbell.
 */

export const TIER_SYNC_KEYS = {
  /** Written by the tab that STARTS checkout, so every open tab knows an upgrade is in flight. */
  pending: 'credia:upgrade-pending',
  /** Written by the success tab AFTER it confirms PRO from /me — the confirmation signal. */
  synced: 'credia:tier-synced',
} as const;

/** Intent flag lifetime — after this, a stale "pending" is ignored and cleaned up. */
export const PENDING_TTL_MS = 30 * 60 * 1000; // 30 min
/** A `tier-synced` write older than this is treated as a replay and ignored. */
export const SYNCED_REPLAY_MS = 60 * 1000; // 60 s

function now(): number {
  return Date.now();
}

function readTs(key: string): number | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {return null;}
    const ts = Number.parseInt(raw, 10);
    return Number.isFinite(ts) ? ts : null;
  } catch {
    return null;
  }
}

/** Tab A: record that an upgrade has been started (called when checkout opens). */
export function markUpgradePending(): void {
  try {
    localStorage.setItem(TIER_SYNC_KEYS.pending, String(now()));
  } catch {
    /* localStorage unavailable (private mode / disabled) — sync degrades to focus reconcile at most */
  }
}

/** Tab B: signal that the tier is confirmed PRO on the server (called after the success poll). */
export function markTierSynced(): void {
  try {
    localStorage.setItem(TIER_SYNC_KEYS.synced, String(now()));
  } catch {
    /* no-op */
  }
}

/** Clear both keys — called once the original tab has reconciled to PRO. */
export function clearTierSync(): void {
  try {
    localStorage.removeItem(TIER_SYNC_KEYS.pending);
    localStorage.removeItem(TIER_SYNC_KEYS.synced);
  } catch {
    /* no-op */
  }
}

/** True if an upgrade is in flight and the intent flag has not aged out. */
export function isUpgradePending(): boolean {
  const ts = readTs(TIER_SYNC_KEYS.pending);
  if (ts == null) {return false;}
  if (now() - ts > PENDING_TTL_MS) {
    // Stale — clean it up so it can't retrigger later.
    clearTierSync();
    return false;
  }
  return true;
}

/** True if the given `tier-synced` timestamp string is fresh enough to act on (replay guard). */
export function isFreshSyncedValue(value: string | null): boolean {
  if (!value) {return false;}
  const ts = Number.parseInt(value, 10);
  if (!Number.isFinite(ts)) {return false;}
  return now() - ts <= SYNCED_REPLAY_MS;
}
